import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import useSWR from 'swr';

import { getLatestProxyBid } from 'components/auctions/getLatestProxyBid';
import { apiUrls } from 'lib/api_urls';
import { ProxyBid } from 'types';

import { useBezelUser } from './bezelUser';

import { clientApiGet } from '../lib/api_helper';

type ProxyBidsContextProps = {
  proxyBid?: ProxyBid;
  isLoading: boolean;
  refetchProxyBids: () => Promise<ProxyBid[] | undefined>;
};
const ProxyBidsContext = createContext<ProxyBidsContextProps>({
  proxyBid: undefined,
  isLoading: false,
  refetchProxyBids: () => Promise.resolve([])
});

type ProxyBidsContextProviderProps = { listingId: number } & PropsWithChildren;

export const ProxyBidsContextProvider: FC<ProxyBidsContextProviderProps> = ({ children, listingId }) => {
  const { user } = useBezelUser();
  const userId = user?.buyerProfile?.id;

  const { data, mutate, isLoading } = useSWR<ProxyBid[]>(
    userId ? apiUrls.proxyBid(userId, listingId, { serverApi: true }) : null,
    clientApiGet
  );

  const value = useMemo(
    () => ({
      proxyBid: getLatestProxyBid(data),
      isLoading,
      refetchProxyBids: mutate
    }),
    [data, isLoading, mutate]
  );

  return <ProxyBidsContext.Provider value={value}>{children}</ProxyBidsContext.Provider>;
};

export const useProxyBids = () => {
  const proxyBids = useContext(ProxyBidsContext);

  if (!proxyBids) {
    throw new Error('ProxyBidsContext used outside of ProxyBidsContext.Provider!');
  }

  return proxyBids;
};
